import { useEffect, useId, useRef, type ReactNode } from 'react';
import './ConfirmCard.css';
import '../../features/compensation/compensation.css';

/** Round status icon used by the confirmation and result cards: "!" for a question, a tick for a completed action. */
function CardIcon({ kind }: { kind: 'ask' | 'done' }) {
  return (
    <span className={`confirm-card-icon confirm-card-icon--${kind}`} aria-hidden="true">
      {kind === 'ask' ? '!' : '✓'}
    </span>
  );
}

export interface ConfirmCardProps {
  title: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
  confirmTone: 'save' | 'danger';
  busy: boolean;
  /** Shown inside the card, for example a refusal from the server. */
  error?: string | null;
  /** Extra content between the text and the buttons, for example a reason field. */
  children?: ReactNode;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The Figma confirmation card ("Save Changes?", "Are you sure?"): an alert dialog that takes focus on its safe (cancel) button and closes with Escape.
 * It never does the work itself: the caller's `onConfirm` does, and shows `error` here when the server refuses.
 */
export function ConfirmCard({
  title, body, cancelLabel, confirmLabel, confirmTone, busy, error, children, confirmDisabled = false, onCancel, onConfirm,
}: ConfirmCardProps) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  return (
    <div className="compensation-modal-backdrop confirm-card-backdrop" role="presentation">
      <div
        className="confirm-card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) onCancel();
        }}
      >
        <CardIcon kind="ask" />
        <h3 id={titleId}>{title}</h3>
        <p id={bodyId}>{body}</p>
        {children && <div className="confirm-card-body">{children}</div>}
        {error && <p className="error" role="alert">{error}</p>}
        <div className="confirm-card-actions">
          <button ref={cancelRef} type="button" className="confirm-btn confirm-btn--grey" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button type="button" className={`confirm-btn confirm-btn--${confirmTone}`} onClick={onConfirm} disabled={busy || confirmDisabled}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The Figma success card ("Changes Saved!", "Benefit / Deduction Deleted!"). Announced politely, dismissed by a click or after a few seconds. */
export function ResultCard({ title, text, onDismiss }: { title: string; text: string; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  return (
    <div className="compensation-modal-backdrop confirm-card-backdrop" role="presentation" onClick={onDismiss}>
      <div className="confirm-card" role="status" aria-live="polite">
        <CardIcon kind="done" />
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </div>
  );
}
